import { useEffect, useRef } from "react";
import type { Interpreter, VnProgram } from "../engine/Interpreter";
import type { Blackboard } from "../engine/Blackboard";
import type { EventBus } from "../engine/EventBus";

interface HotReloadOptions {
  interpreter: Interpreter;
  bb: Blackboard;
  bus: EventBus;
  scriptName: string;
  onReload?: (program: VnProgram) => void;
}

const WS_URL = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;
const RECONNECT_DELAY = 3000;

export function useHotReload({ interpreter, bb, bus, scriptName, onReload }: HotReloadOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    let disposed = false;

    function connect() {
      if (disposed) return;
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.info("[HotReload] Connected to dev server");
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "recompiled" && msg.script === scriptName) {
            handleRecompile();
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (!disposed) {
          reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    async function handleRecompile() {
      try {
        const res = await fetch(`/json/${scriptName}.vnp.json`);
        if (!res.ok) return;
        const program: VnProgram = await res.json();
        if (program.format !== "vn-program") return;

        const currentScene = interpreter.currentScene;
        const currentBeat = interpreter.currentBeat;
        const bbSnapshot = bb.snapshot();
        const bbTypes = bb.snapshotTypes();
        const visits = interpreter.getVisitsMap();
        const usedChoices = interpreter.getUsedChoices();
        const pending = interpreter.getPending();

        interpreter.load(program);

        // Best-effort restore: if the scene still exists, teleport back
        if (program.scenes[currentScene]) {
          const maxBeat = program.scenes[currentScene].beats.length - 1;
          const safeBeat = Math.min(currentBeat, maxBeat);
          bb.restore(bbSnapshot);
          bb.restoreTypes(bbTypes);
          interpreter.restoreState(currentScene, safeBeat, pending, visits, usedChoices);
        }

        onReload?.(program);
        bus.emit("dialogue.narrate", {
          template: "[Hot Reload] Script updated.",
          resolved: "[Hot Reload] Script updated.",
          id: null,
        });
        console.info("[HotReload] Reloaded", scriptName);
      } catch (err) {
        console.warn("[HotReload] Failed to reload:", err);
      }
    }

    connect();

    return () => {
      disposed = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [interpreter, bb, bus, scriptName, onReload]);
}
